// @flow

import mixpanel from 'mixpanel-browser';
import type { cardDetailsType } from '../../types/card/card_details_type';
import { ButtonActionDictionary } from './ButtonActions';
import { TrackedEvents } from './TrackingEnums';
import type {
  EventTrackingContextType,
  ListingDetailContextType,
} from './TrackingContext';
import { isTestEnvironment } from '../settings/environmentSettings';
import { ListingDetailActionDictionary } from './ListingDetailActions';

const trackEvent = (eventName: string, eventProperties: Object) => {
  if (eventProperties?.dataLayer) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventProperties.dataLayer);
  } else {
    mixpanel.track(eventName, eventProperties);
  }

  window.__trackEventAttachable?.(eventName, eventProperties);
};

if (isTestEnvironment()) {
  window.__trackEventAttachable = (_eventName, _eventProperties) => {};
}

const trackCardClickEvent = (
  cardDetails: cardDetailsType,
  trackingContext?: EventTrackingContextType,
) => {
  const defaultEventProperties = getCardClickEventDetails(cardDetails);
  const eventProperties = trackingContext?.shouldOverride
    ? trackingContext.eventProperties
    : { ...defaultEventProperties, ...trackingContext?.eventProperties };

  trackEvent(TrackedEvents.CardSelected, eventProperties);
};

const trackButtonSelectedEvent = (
  buttonAction: string,
  metaData: any,
  trackingContext?: EventTrackingContextType,
) => {
  let defaultEventProperties = {
    button_action: buttonAction,
  };
  const defaultFromButtonActions = ButtonActionDictionary[buttonAction]?.(
    metaData,
  );
  if (defaultFromButtonActions)
    defaultEventProperties = {
      ...defaultEventProperties,
      ...defaultFromButtonActions,
    };

  const eventProperties = trackingContext?.shouldOverride
    ? trackingContext.eventProperties
    : { ...defaultEventProperties, ...trackingContext?.eventProperties };

  trackEvent(TrackedEvents.ButtonSelected, eventProperties);
};

const trackToggleSelectedEvent = (
  toggleAction: string,
  toggledTo: any,
  trackingContext?: EventTrackingContextType,
) => {
  const defaultEventProperties = {
    toggle_action: toggleAction,
    toggled_to: toggledTo,
  };

  const eventProperties = trackingContext?.shouldOverride
    ? trackingContext.eventProperties
    : { ...defaultEventProperties, ...trackingContext?.eventProperties };

  trackEvent(TrackedEvents.ToggleSelected, eventProperties);
};

const trackStepValidEvent = (
  stepValidAction: string,
  trackingContext?: EventTrackingContextType,
) => {
  const defaultEventProperties = {
    step_valid: stepValidAction,
  };

  const eventProperties = trackingContext?.shouldOverride
    ? trackingContext.eventProperties
    : { ...defaultEventProperties, ...trackingContext?.eventProperties };

  trackEvent(TrackedEvents.StepValid, eventProperties);
};

const trackListingDetailPageVisit = (
  listingDetailAction: string,
  trackingContext: ListingDetailContextType,
) => {
  const eventProperties = ListingDetailActionDictionary[listingDetailAction]?.(
    trackingContext,
  );
  trackEvent(TrackedEvents.PageVisited, eventProperties);
};

function getCardClickEventDetails(cardDetails: cardDetailsType): Object {
  const eventProperties = {
    card_type: cardDetails.card_type,
    id: cardDetails.id,
    uid: cardDetails.uid,
    category_id: cardDetails.category,
    promoted: cardDetails.is_promoted > 0,
    nationwide: cardDetails.nationwide,
  };
  return eventProperties;
}

export {
  trackEvent,
  trackCardClickEvent,
  trackButtonSelectedEvent,
  trackToggleSelectedEvent,
  trackStepValidEvent,
  trackListingDetailPageVisit,
};
